import React, { useEffect, useState, useCallback } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import useScrollDirection from 'ui/src/hooks/useScrollDirection';

import { HeaderContextProvider } from './_context';
import useHeader from './hooks/useHeader';

export type HeaderProps = {
  enableHeaderBehavior?: boolean;
  animatedHeader?: boolean;
};

export type HeaderContainerProps = HeaderProps & {
  children: React.ReactNode;
};

function HeaderContainer({ children, enableHeaderBehavior = false, animatedHeader = true }: HeaderContainerProps) {
  const { isMobileMenuOpen } = useHeader();
  const [scrolled, setScrolled] = useState(false);
  const { isScrollingDown } = useScrollDirection();

  const onScroll = useCallback(() => {
    const { scrollY } = window;

    if ((scrolled && !scrollY) || (!scrolled && scrollY)) {
      setScrolled(window.scrollY !== 0);
    }
  }, [scrolled]);

  useEffect(() => {
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  return (
    <HeaderContextProvider>
      <header
        className={twMerge(
          'mb-10 h-20',
          'z-40 text-sm',
          { 'sticky top-0 transform duration-300': enableHeaderBehavior || animatedHeader },
          'bg-site-neutrals-0 text-site-neutrals-900',
          {
            'pointer-events-none opacity-0': isScrollingDown && !isMobileMenuOpen && animatedHeader,
            'drop-shadow-lg': scrolled && animatedHeader,
          },
        )}
      >
        <div className={twMerge('relative box-content flex h-full items-center justify-between px-8 xl:mx-auto')}>
          {children}
        </div>
      </header>
    </HeaderContextProvider>
  );
}

export default HeaderContainer;
